import { HubEnrollmentDetail } from "@/components/hub/HubEnrollmentDetail";
import { PageLayout } from "@/components/layout";
import { ErrorState } from "@/components/ui/error-state";
import { Skeleton } from "@/components/ui/skeleton";
import { useHubEnrollmentById } from "@/hooks/useHubEnrollments";
import { useAuthContext } from "@niutraan/hooks";
import { Book } from "lucide-react";
import { useParams } from "react-router-dom";

export default function HubEnrollmentDetailScreen() {
  const { id: enrollmentId } = useParams<{ id: string }>();
  const { user } = useAuthContext();

  // Fetch enrollment data
  const {
    data: enrollment,
    isLoading: isEnrollmentLoading,
    error: enrollmentError,
    refetch: refetchEnrollment,
    isRefetching: isEnrollmentRefetching,
  } = useHubEnrollmentById(enrollmentId ?? "");

  // Show loading state
  if (isEnrollmentLoading && !isEnrollmentRefetching) {
    return (
      <PageLayout
        title="Loading Enrollment"
        description="Please wait..."
        breadcrumbs={[
          { label: "Enrollments", href: "/admin/hubs/enrollments" },
          { label: `${user?.firstName} Enrollment Details` },
        ]}
      >
        <div className="space-y-4">
          <Skeleton className="h-[40px] w-[200px]" />
          <Skeleton className="h-[20px] w-[300px]" />
          <Skeleton className="h-[200px] w-full" />
          <Skeleton className="h-[100px] w-full" />
        </div>
      </PageLayout>
    );
  }

  // Show error state
  if (enrollmentError) {
    return (
      <PageLayout
        title="Error"
        breadcrumbs={[
          { label: "Enrollments", href: "/admin/hubs/enrollments" },
          { label: `${user?.firstName} Enrollment Details` },
        ]}
      >
        <ErrorState
          title="Error loading Enrollment details"
          error={enrollmentError}
          icon={<Book className="h-12 w-12 text-destructive" />}
          onAction={() => refetchEnrollment()}
          actionText="Go Back"
          containerSize="w-full"
        />
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title="Enrollment Details"
      breadcrumbs={[
        { label: "Enrollments", href: "/admin/hubs/enrollments" },
        {
          label: `${enrollment?.user?.firstName || "User"} Enrollment Details`,
        },
      ]}
    >
      {enrollment && (
        <HubEnrollmentDetail
          enrollment={enrollment}
          enrollmentId={enrollmentId ?? ""}
          showHourAdjustments={true}
          onRefreshAdjustments={() => refetchEnrollment()}
        />
      )}
    </PageLayout>
  );
}
